import request from '@/utils/request';
import type { TraceItem } from '@/utils/document/traceBlocks';

/** 获取当前的用户 GET /api/currentUser */
export const currentUser = (options?: { [key: string]: any }): Promise<{ data: API.CurrentUser }> =>
  request.get('/api/auth/currentUser', { fetchOptions: { ...(options || {}) } });

/** 登录接口 POST /api/auth/login */
export function login(body: API.LoginParams): Promise<API.LoginResult> {
  return request('/api/auth/login', {
    method: 'POST',
    data: body,
  });
}

/** 退出登录接口 POST /api/auth/logout */
export async function logout(): Promise<Record<string, any>> {
  // 携带 refresh_token，登出时后端会将 accessToken 和 refreshToken 同时失效
  const refreshToken = localStorage.getItem('refresh_token');
  const res = await request<Record<string, any>>('/api/auth/logout', {
    method: 'POST',
    data: { refresh_token: refreshToken },
  });

  // 清除本地存储的 token
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  // 清除 sessionStorage
  sessionStorage.clear();

  // 清除所有 localStorage
  // localStorage.clear();

  // 清除缓存
  if (window.caches) {
    caches.keys().then((keys) => {
      keys.forEach((key) => {
        caches.delete(key);
      });
    });
  }

  return res;
}

/** 注册接口 POST /api/auth/register */
export function register(body: API.RegisterParams): Promise<API.RegisterResult> {
  return request('/api/auth/register', {
    method: 'POST',
    data: body,
  });
}

/** 获取通知列表 GET /api/user/notifications */
export function getNotifications(params?: {
  current?: number;
  pageSize?: number;
}): Promise<API.NotificationList> {
  return request('/api/user/notifications', {
    method: 'GET',
    params,
  });
}

/** 标记通知为已读 POST /api/user/notifications/:id/read */
export function markNotificationAsRead(id: string): Promise<API.MarkAsReadResult> {
  return request(`/api/user/notifications/${id}/read`, {
    method: 'POST',
  });
}

/** 获取通知数量 GET /api/user/notifications/count */
export function getNotificationCount(): Promise<API.NotificationCount> {
  return request('/api/user/notifications/count', {
    method: 'GET',
  });
}

/** 删除所有已读通知 DELETE /api/user/notifications/read */
export function deleteReadNotifications(): Promise<API.DeleteResult> {
  return request('/api/user/notifications/read', {
    method: 'DELETE',
  });
}

/** 获取用户设置 GET /api/user/settings */
export function getUserSettings(): Promise<{
  data: API.UserSettings;
  success: boolean;
}> {
  return request('/api/user/settings', {
    method: 'GET',
  });
}

/** 更新用户设置 PUT /api/user/settings */
export function updateUserSettings(data: API.UserSettings): Promise<API.UpdateSettingsResult> {
  return request('/api/user/settings', {
    method: 'PUT',
    data,
  });
}

/** 更新用户密码 PUT /api/user/password */
export function updateUserPassword(
  data: API.UpdatePasswordParams,
): Promise<API.UpdatePasswordResult> {
  return request('/api/user/password', {
    method: 'PUT',
    data,
  });
}

/** 上传签名图片 POST /api/user/signature */
export function uploadSignature(file: FormData): Promise<{
  url: string;
  success: boolean;
}> {
  return request('/api/user/signature', {
    method: 'POST',
    data: file,
  });
}

// Project APIs
/** 获取项目列表 GET /api/projects */
export function getProjects(params: {
  search?: string;
  current?: number;
  pageSize?: number;
  isAspiceView?: boolean;
}): Promise<{
  data: API.Project[];
  success: boolean;
  total: number;
}> {
  return request('/api/projects', {
    method: 'GET',
    params,
  });
}

/** 获取项目详情 GET /api/projects/:id */
export function getProject(id: number): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request(`/api/projects/${id}`, {
    method: 'GET',
  });
}

/** 创建项目 POST /api/projects */
export function createProject(data: API.CreateProjectParams): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request('/api/projects', {
    method: 'POST',
    data,
  });
}

/** 更新项目 PUT /api/projects/:id */
export function updateProject(
  id: number,
  data: API.UpdateProjectParams,
): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request(`/api/projects/${id}`, {
    method: 'PUT',
    data,
  });
}

/** 删除项目 DELETE /api/projects/:id */
export function deleteProject(id: number): Promise<{
  success: boolean;
}> {
  return request(`/api/projects/${id}`, {
    method: 'DELETE',
  });
}

/** 获取项目概览数据 GET /api/projects/:id/overview */
export function getProjectOverview(projectId: number): Promise<{
  data: API.ProjectOverviewData;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/overview`, {
    method: 'GET',
  });
}

/** 获取项目过程进度 GET /api/projects/:id/processes/:processId/progress */
export function getProjectProcessProgress(
  projectId: number,
  processId: string,
): Promise<{
  progress: number;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/processes/${processId}/progress`, {
    method: 'GET',
  });
}

/** 按所选文档复制草稿 POST /api/projects/:id/documents/change-drafts */
export function createDocumentChangeDrafts(
  projectId: number,
  documentIds: number[],
): Promise<{
  data: API.ASPICEDocument[];
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/documents/change-drafts`, {
    method: 'POST',
    data: { documentIds },
  });
}

/** 分页获取项目已受控文档 GET /api/projects/:id/documents/controlled */
export function getControlledDocuments(
  projectId: number,
  params?: {
    current?: number;
    pageSize?: number;
    search?: string;
  },
): Promise<{
  data: {
    list: API.ASPICEDocument[];
    total: number;
    page: number;
    pageSize: number;
  };
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents/controlled`, {
    method: 'GET',
    params,
  });
}

/** 获取项目文档列表 GET /api/projects/:id/documents */
export function getProjectDocuments(
  projectId: number,
  params?: {
    search?: string;
    processCode?: string;
    categoryId?: string;
    statusList?: API.DocumentStatusCode[];
  },
): Promise<{
  data: API.ASPICEDocument[];
  total: number;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents`, {
    method: 'GET',
    params,
  });
}

/** 创建项目文档 POST /api/projects/:id/documents */
export function createProjectDocument(
  projectId: number,
  data: {
    title: string;
    processId: string;
    categoryId: string;
    templateId?: number;
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents`, {
    method: 'POST',
    data,
  });
}

// ASPICE APIs
/** 获取模板列表 GET /api/aspice/templates */
export function getTemplates(params: {
  current?: number;
  pageSize?: number;
  search?: string;
  processId?: string;
  categoryId?: string;
  tags?: string[];
}): Promise<{
  data: API.ASPICETemplate[];
  total: number;
  success: boolean;
}> {
  return request('/api/aspice/templates', {
    method: 'GET',
    params,
  });
}

/** 获取模板详情 GET /api/aspice/templates/:id */
export function getTemplate(id: number): Promise<{
  data: API.ASPICETemplate;
  success: boolean;
}> {
  return request(`/api/aspice/templates/${id}`, {
    method: 'GET',
  });
}

/** 获取模板内容 GET /api/aspice/templates/:id/content */
export function getTemplateContent(id: number): Promise<{
  data: {
    content: any; // DOCX 类型为树形结构，XLSX 类型为表格结构
    format: API.DocumentFormat;
  };
  success: boolean;
}> {
  return request(`/api/aspice/templates/${id}/content`, {
    method: 'GET',
  });
}

/** 获取所有标签 GET /api/aspice/templates/tags */
export function getAllTags(): Promise<{
  data: string[];
  success: boolean;
}> {
  return request('/api/aspice/templates/tags', {
    method: 'GET',
  });
}

/** 创建模板 POST /api/aspice/templates */
export function createTemplate(data: API.CreateTemplateParams): Promise<{
  data: API.ASPICETemplate;
  success: boolean;
}> {
  return request('/api/aspice/templates', {
    method: 'POST',
    data,
  });
}

/** 更新模板 PUT /api/aspice/templates/:id */
export function updateTemplate(
  id: number,
  data: API.UpdateTemplateParams,
): Promise<{
  data: API.ASPICETemplate;
  success: boolean;
}> {
  return request(`/api/aspice/templates/${id}`, {
    method: 'PUT',
    data,
  });
}

/** 删除模板 DELETE /api/aspice/templates/:id */
export function deleteTemplate(id: number): Promise<{
  success: boolean;
}> {
  return request(`/api/aspice/templates/${id}`, {
    method: 'DELETE',
  });
}

/** 下载模板 GET /api/aspice/templates/:id/download */
export function downloadTemplate(id: number): Promise<Blob> {
  return request(`/api/aspice/templates/${id}/download`, {
    method: 'GET',
    responseType: 'blob',
  });
}

/** 下载全部模板元数据 SQL（SQLite）GET /api/aspice/templates/backup/sql */
export function downloadTemplatesBackupSql(): Promise<Blob> {
  return request('/api/aspice/templates/backup/sql', {
    method: 'GET',
    responseType: 'blob',
    fetchOptions: {
      skipErrorHandler: true,
    },
    validateStatus: () => true,
  });
}

/** 下载全部模板 MongoDB 文档内容 JSON GET /api/aspice/templates/backup/mongo-json */
export function downloadTemplatesBackupMongoJson(): Promise<Blob> {
  return request('/api/aspice/templates/backup/mongo-json', {
    method: 'GET',
    responseType: 'blob',
    fetchOptions: {
      skipErrorHandler: true,
    },
    validateStatus: () => true,
  });
}

/** 获取过程列表 GET /api/aspice/processes */
export function getProcesses(): Promise<{
  data: API.ASPICEProcess[];
  success: boolean;
}> {
  return request('/api/aspice/processes', {
    method: 'GET',
  });
}

/** 获取带模板数量的过程列表 GET /api/aspice/processes/template-counts */
export function getProcessesWithTemplateCounts(): Promise<{
  data: Array<{
    process: API.ASPICEProcess;
    count: number;
  }>;
  success: boolean;
}> {
  return request('/api/aspice/processes/template-counts', {
    method: 'GET',
  });
}

/** 获取角色列表 GET /api/aspice/roles */
export function getRoles(): Promise<{
  data: API.ASPICERole[];
  total: number;
  success: boolean;
}> {
  return request('/api/aspice/roles', {
    method: 'GET',
  });
}

/** 创建角色 POST /api/aspice/roles */
export function createRole(data: API.CreateRoleParams): Promise<{
  data: API.ASPICERole;
  success: boolean;
}> {
  return request('/api/aspice/roles', {
    method: 'POST',
    data,
  });
}

/** 更新角色 PUT /api/aspice/roles/:id */
export function updateRole(
  id: string,
  data: API.UpdateRoleParams,
): Promise<{
  data: API.ASPICERole;
  success: boolean;
}> {
  return request(`/api/aspice/roles/${id}`, {
    method: 'PUT',
    data,
  });
}

/** 删除角色 DELETE /api/aspice/roles/:id */
export function deleteRole(id: string): Promise<{
  success: boolean;
}> {
  return request(`/api/aspice/roles/${id}`, {
    method: 'DELETE',
  });
}

/** 获取程序文件列表 GET /api/aspice/process-documents */
export function getProcessDocuments(params?: { search?: string }): Promise<{
  data: API.ASPICEDocument[];
  total: number;
  success: boolean;
}> {
  return request('/api/aspice/process-documents', {
    method: 'GET',
    params,
  });
}

/** 获取名为 PROCESS_DOCUMENT 的项目 GET /api/projects/process-document */
export function getProcessDocumentProject(): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request('/api/aspice/projects/process-document', {
    method: 'GET',
  });
}

/** 获取审批任务列表 GET /api/review-tasks */
export function getReviewTasks(params?: {
  search?: string;
  current?: number;
  pageSize?: number;
}): Promise<API.ReviewTaskListResult> {
  return request('/api/projects/documents/review-tasks', {
    method: 'GET',
    params,
  });
}

// @Deprecated
/** 提交审批结果 POST /api/projects/documents/:id/review-result */
export function submitReviewResult(params: API.ReviewTaskParams): Promise<API.ReviewTaskResult> {
  return request(`/api/projects/documents/${params.id}/review-result`, {
    method: 'POST',
    data: {
      status: params.status,
      comment: params.comment,
      role: params.role,
    },
  });
}

/** 提交文档评审 POST /api/projects/:projectId/documents/:docId/review */
export function submitDocumentReview(params: API.SubmitReviewParams): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${params.projectId}/documents/${params.documentId}/review`, {
    method: 'POST',
    data: {
      reviewers: params.reviewers,
      reviewLeader: params.reviewLeader,
      auditor: params.auditor,
      approver: params.approver,
    },
  });
}

/** 更新文档评审 PUT /api/projects/:projectId/documents/:docId/review */
export function updateDocumentReview(params: API.SubmitReviewParams): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${params.projectId}/documents/${params.documentId}/review`, {
    method: 'PUT',
    data: {
      reviewers: params.reviewers,
      reviewLeader: params.reviewLeader,
      auditor: params.auditor,
      approver: params.approver,
    },
  });
}

/** 忘记密码接口 POST /api/auth/forgot-password */
export function forgotPassword(email: string): Promise<API.ForgotPasswordResult> {
  return request('/api/auth/forgot-password', {
    method: 'POST',
    data: { email },
  });
}

/** 获取当前项目 GET /api/projects/current */
export function getCurrentProject(): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request('/api/projects/current', {
    method: 'GET',
  });
}

/** 切换当前项目 POST /api/projects/:id/switch */
export function switchProject(projectId: number): Promise<{
  data: API.Project;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/switch`, {
    method: 'POST',
  });
}

/** 获取系统概览统计数据 GET /api/system/stats */
export function getSystemStats(): Promise<{
  data: API.SystemStats;
  success: boolean;
}> {
  return request('/api/system/stats', {
    method: 'GET',
  });
}

/** 获取企业信息 GET /api/system/company */
export function getCompanyInfo(): Promise<{
  data: API.CompanyInfo;
  success: boolean;
}> {
  return request('/api/system/company', {
    method: 'GET',
  });
}

/** 更新企业信息 PUT /api/system/company */
export function updateCompanyInfo(data: API.CompanyInfo): Promise<{
  success: boolean;
}> {
  return request('/api/system/company', {
    method: 'PUT',
    data,
  });
}

/** 获取系统用户列表 GET /api/system/users */
export function getSystemUsers(params?: { search?: string }): Promise<{
  data: API.SystemUser[];
  total: number;
  success: boolean;
}> {
  return request('/api/system/users', {
    method: 'GET',
    params,
  });
}

/** 更新用户状态 PUT /api/system/users/:id/status */
export function updateUserStatus(
  id: string,
  isActive: boolean,
): Promise<{
  success: boolean;
}> {
  return request(`/api/system/users/${id}/status`, {
    method: 'PUT',
    data: { isActive },
  });
}

/** 删除用户 DELETE /api/system/users/:id */
export function deleteUser(id: string): Promise<{
  success: boolean;
}> {
  return request(`/api/system/users/${id}`, {
    method: 'DELETE',
  });
}

/** 更新用户角色 PUT /api/system/users/:id/role */
export function updateUserRole(
  id: string,
  role: 'user' | 'aspice_specialist',
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/system/users/${id}/role`, {
    method: 'PUT',
    data: { role },
  });
}

/** 管理员重置指定用户密码 PUT /api/system/users/:id/password
 *  不传 password 时由后端生成随机密码；新密码始终在 data.password 中回传
 */
export function resetUserPassword(
  id: string,
  password?: string,
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    password: string;
    autoGenerated: boolean;
    emailSent: boolean;
  };
}> {
  return request(`/api/system/users/${id}/password`, {
    method: 'PUT',
    data: { password },
  });
}

/** 创建用户 POST /api/system/users */
export function createUser(
  email: string,
  department?: string,
  clientUrl?: string,
): Promise<{
  success: boolean;
  message?: string;
  data?: API.SystemUser & { clientUrl?: string };
}> {
  return request('/api/system/users', {
    method: 'POST',
    data: { email, department, clientUrl },
  });
}

/** 获取邮箱设置 GET /api/system/email-settings */
export function getEmailSettings(): Promise<{
  data: API.EmailSettings;
  success: boolean;
}> {
  return request('/api/system/email-settings', {
    method: 'GET',
  });
}

/** 更新邮箱设置 PUT /api/system/email-settings */
export function updateEmailSettings(data: API.EmailSettings): Promise<{
  success: boolean;
}> {
  return request('/api/system/email-settings', {
    method: 'PUT',
    data,
  });
}

/** 获取AI设置 GET /api/system/ai-settings */
export function getAISettings(): Promise<{
  data: API.AISettings;
  success: boolean;
}> {
  return request('/api/system/ai-settings', {
    method: 'GET',
  });
}

/** 更新AI设置 PUT /api/system/ai-settings */
export function updateAISettings(data: API.AISettings): Promise<{
  success: boolean;
}> {
  return request('/api/system/ai-settings', {
    method: 'PUT',
    data,
  });
}

/** 获取授权信息 GET /api/system/license */
export function getLicenseInfo(): Promise<{
  data: API.LicenseInfo;
  success: boolean;
}> {
  return request('/api/system/license', {
    method: 'GET',
  });
}

/** 更新授权信息 PUT /api/system/license */
export function updateLicenseInfo(data: { licenseKey: string }): Promise<{
  success: boolean;
  message: string;
  data?: {
    customerInfo?: string;
    validUntil?: string;
    maxUsers?: number;
  };
}> {
  return request('/api/system/license', {
    method: 'PUT',
    data,
  });
}

/** 获取文档详情 GET /api/documents/:id */
export function getDocumentInfo(id: string): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
}> {
  return request(`/api/documents/${id}`, {
    method: 'GET',
  });
}

/** 获取文档内容 GET /api/documents/:id/content */
export function getDocumentContent(id: string): Promise<{
  data: {
    content: any; // DOCX 类型为树形结构，XLSX 类型为表格结构
    format: API.DocumentFormat;
    largeFile?: boolean; // 大文件：正文在后端按章节拆分存储，返回的仍是完整章节树
  };
  success: boolean;
}> {
  return request(`/api/documents/${id}/content`, {
    method: 'GET',
  });
}

/** 章节目录节点（不含正文） */
export interface DocumentOutlineNode {
  key: string;
  title: string;
  level: number;
  children: DocumentOutlineNode[];
}

/** 获取文档章节目录 GET /api/documents/:id/outline */
export function getDocumentOutline(id: string): Promise<{
  data: {
    outline: DocumentOutlineNode[];
    largeFile: boolean;
  };
  success: boolean;
}> {
  return request(`/api/documents/${id}/outline`, {
    method: 'GET',
  });
}

/** 更新文档内容 PUT /api/documents/:id/content */
export function updateDocumentContent(
  id: string,
  content: any,
  revisionDescription?: string,
): Promise<{
  success: boolean;
  message: string;
}> {
  return request(`/api/documents/${id}/content`, {
    method: 'PUT',
    data: { content, revisionDescription },
  });
}

/** 删除文档内容 DELETE /api/documents/:id/content */
export function deleteDocumentContent(id: string): Promise<{
  success: boolean;
  message: string;
}> {
  return request(`/api/documents/${id}/content`, {
    method: 'DELETE',
  });
}

/** 创建文档新版本 POST /api/projects/:projectId/documents/:docId/versions */
export function createDocumentVersion(
  projectId: number,
  docId: number,
  data: {
    version?: string;
    description?: string;
    customDocumentCode?: string;
    /** 为 true 时忽略 version，按 x.y+1 建草稿，并把当前文档锁为草稿 */
    lockCurrent?: boolean;
    revisionDescription?: string;
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents/${docId}/versions`, {
    method: 'POST',
    data,
  });
}

/** 从模板创建文档 POST /api/projects/:projectId/documents/from-template */
export function createDocumentFromTemplate(
  projectId: number,
  data: {
    templateId: number;
    version: string;
    customDocumentCode?: string;
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents/from-template`, {
    method: 'POST',
    data,
  });
}

/** 从本地文件创建文档 POST /api/projects/:projectId/documents/from-local */
export function createDocumentLocal(
  projectId: number,
  data: {
    title: string;
    processId: string;
    categoryId: string;
    version: string;
    content: any;
    format: API.DocumentFormat;
    keepOriginal?: boolean;
    customDocumentCode?: string;
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/documents/from-local`, {
    method: 'POST',
    data,
  });
}

/** 获取项目成员 GET /api/projects/:id/members */
export function getProjectMembers(projectId: number): Promise<{
  data: API.TeamMember[];
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/members`, {
    method: 'GET',
  });
}

/** 获取项目邀请未注册成员列表 GET /api/projects/:id/invitations */
export function getProjectInvitations(projectId: number): Promise<{
  data: API.TeamInvitation[];
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/invitations`, {
    method: 'GET',
  });
}

/** 添加项目成员 POST /api/projects/:id/members */
export function addProjectMember(
  projectId: number,
  data: API.AddTeamMemberParams,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/members`, {
    method: 'POST',
    data,
  });
}

/** 更新项目成员 PUT /api/projects/:projectId/members/:userId */
export function updateProjectMember(
  projectId: number,
  userId: number,
  data: API.UpdateTeamMemberParams,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/members/${userId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除项目成员 DELETE /api/projects/:projectId/members/:userId */
export function deleteProjectMember(
  projectId: number,
  userId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/members/${userId}`, {
    method: 'DELETE',
  });
}

/** 获取项目过程列表 GET /api/projects/:id/processes */
export function getProjectProcesses(projectId: number): Promise<{
  data: API.ASPICEProcess[];
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/processes`, {
    method: 'GET',
  });
}

/** 更新项目过程 PUT /api/projects/:id/processes/:processCode */
export function updateProjectProcess(
  projectId: number,
  processCode: string,
  data: {
    status?: string;
    owners?: { id: number }[];
    ownerId?: number | null;
  },
): Promise<{
  data: API.ASPICEProcess;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/processes/${processCode}`, {
    method: 'PUT',
    data,
  });
}

/** 获取文档评审状态 GET /api/projects/documents/:id/review-result */
export function getDocumentReviewStatus(id: string): Promise<{
  data: API.ReviewStatus;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/documents/${id}/review-result`, {
    method: 'GET',
  });
}

/** 获取文档评审记录 GET /api/projects/documents/:id/review-records */
export function getDocumentReviewRecords(id: string): Promise<{
  data: {
    documentId: number;
    documentTitle: string;
    documentCode: string;
    reviewers: Array<{
      userId: number;
      userName: string;
      role: string;
      status: string;
      comment: string;
      reviewTime: string;
    }>;
    auditor: {
      userId: number;
      userName: string;
      role: string;
      status: string;
      comment: string;
      reviewTime: string;
    } | null;
    approver: {
      userId: number;
      userName: string;
      role: string;
      status: string;
      comment: string;
      reviewTime: string;
    } | null;
    allReviewersApproved: boolean;
    auditorApproved?: boolean;
    anyReviewerRejected: boolean;
  };
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/documents/${id}/review-records`, {
    method: 'GET',
  });
}

/** 删除文档 DELETE /api/documents/:id */
export function deleteDocument(id: number): Promise<{
  success: boolean;
  message: string;
}> {
  return request(`/api/documents/${id}`, {
    method: 'DELETE',
  });
}

/** 更新文档元数据 PUT /api/documents/:id/metadata */
export function updateDocumentMetadata(
  id: string,
  metadata: { sourceType?: string },
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${id}/metadata`, {
    method: 'PUT',
    data: metadata,
  });
}

/** 更新文档描述 PUT /api/documents/:id/description */
export function updateDocumentDescription(
  id: string,
  description: string,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${id}/description`, {
    method: 'PUT',
    data: { description },
  });
}

/** 更新文档标题 PUT /api/documents/:id/title */
export function updateDocumentTitle(
  id: string,
  title: string,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${id}/title`, {
    method: 'PUT',
    data: { title },
  });
}

export function updateCustomDocumentCode(
  id: string,
  customDocumentCode: string | null,
): Promise<{
  success: boolean;
  message?: string;
  data?: { customDocumentCode: string | null };
}> {
  return request(`/api/documents/${id}/custom-document-code`, {
    method: 'PUT',
    data: { customDocumentCode },
  });
}

export function updateDocumentApprovalCode(
  id: string,
  code: string,
): Promise<{
  success: boolean;
  message?: string;
  data?: { code: string };
}> {
  return request(`/api/documents/${id}/approval-code`, {
    method: 'PUT',
    data: { code },
  });
}

// @Deprecated
/** 获取文档修订记录列表 GET /api/documents/:id/revisions */
export function getDocumentRevisions(id: string): Promise<{
  data: API.ASPICEDocument[];
  success: boolean;
}> {
  return request(`/api/documents/${id}/revisions`, {
    method: 'GET',
  });
}

/** 获取文档修订记录列表 GET /api/documents/:id/revision-records */
export function getDocumentRevisionRecords(id: string): Promise<{
  data: API.DocumentRevision[];
  success: boolean;
}> {
  return request(`/api/documents/${id}/revision-records`, {
    method: 'GET',
  });
}

/** 更新文档修订记录描述 PUT /api/documents/:id/revision-records/:revisionId */
export function updateDocumentRevisionRecord(
  id: string | number,
  revisionId: number,
  revisionDescription: string,
): Promise<{
  data?: API.DocumentRevision;
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${id}/revision-records/${revisionId}`, {
    method: 'PUT',
    data: { revisionDescription },
  });
}

/** 删除文档修订记录 DELETE /api/documents/:id/revision-records/:revisionId */
export function deleteDocumentRevisionRecord(
  id: string | number,
  revisionId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${id}/revision-records/${revisionId}`, {
    method: 'DELETE',
  });
}

// 获取RAWXLSX格式文档的二进制数据
export function getDocumentBinaryContent(documentId: string): Promise<Blob> {
  return request(`/api/documents/${documentId}/binary`, {
    method: 'GET',
    responseType: 'blob',
  });
}

// 上传RAWXLSX格式文档的二进制数据
export function uploadDocumentBinaryContent(
  documentId: string,
  file: File,
): Promise<{
  success: boolean;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/api/documents/${documentId}/binary`, {
    method: 'POST',
    data: formData,
  });
}

/** 获取 PDF 源文件 GET /api/documents/:id/source-file */
export function getDocumentSourceFile(documentId: string, download = false): Promise<Blob> {
  return request(`/api/documents/${documentId}/source-file`, {
    method: 'GET',
    params: download ? { download: 1 } : undefined,
    responseType: 'blob',
  });
}

/** 上传 PDF 源文件 POST /api/documents/:id/source-file */
export function uploadDocumentSourceFile(
  documentId: string,
  file: File,
): Promise<{
  success: boolean;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/api/documents/${documentId}/source-file`, {
    method: 'POST',
    data: formData,
  });
}

/** 获取文档追溯关系 GET /api/documents/:id/traceability */
export function getDocumentTraceability(documentId: string): Promise<{
  data: {
    tracedToDocuments: API.ASPICEDocument[];
  };
  success: boolean;
}> {
  return request(`/api/documents/${documentId}/traceability`, {
    method: 'GET',
  });
}

/** 获取文档追溯数据集 GET /api/documents/:id/dataset */
export function getDocumentDataset(documentId: string): Promise<{
  data: {
    dataset: string[];
    allDataset: string[];
    /**
     * 完整追溯候选池：标题 + 正文段落 + 表格行。
     * 「选择追溯项」弹窗据此渲染，正文内容由此可被选为追溯对象。
     * 仅 DOCX 返回，XLSX 为空数组。
     */
    traceItems?: TraceItem[];
    format: string;
    traceabilityColumns: string[];
    sheetId?: string;
    sheetName?: string;
  };
  success: boolean;
}> {
  return request(`/api/documents/${documentId}/dataset`, {
    method: 'GET',
  });
}

/** 批量获取文档内容 POST /api/documents/batch/content */
export function getBatchDocumentContents(documentIds: string[]): Promise<{
  data: {
    [key: string]: {
      content: any;
      format: API.DocumentFormat;
    };
  };
  success: boolean;
}> {
  return request('/api/documents/batch/content', {
    method: 'POST',
    data: { documentIds },
  });
}

/** 设置追溯列 POST /api/projects/:id/traceability/columns */
export function setTraceabilityColumns(
  projectId: number,
  processCode: string,
  columnTitles: string[],
  options?: { sheetId?: string; sheetName?: string },
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/traceability/columns`, {
    method: 'POST',
    data: {
      processCode,
      columnTitles,
      ...(options?.sheetId !== undefined ? { sheetId: options.sheetId } : {}),
      ...(options?.sheetName !== undefined ? { sheetName: options.sheetName } : {}),
    },
  });
}

/** 创建基线 POST /api/projects/:projectId/baselines */
export function createBaseline(
  projectId: number,
  data: {
    title: string;
    description?: string;
  },
): Promise<{
  data: API.Baseline;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/baselines`, {
    method: 'POST',
    data,
  });
}

/** 更新基线 PUT /api/projects/:projectId/baselines/:baselineId */
export function updateBaseline(
  projectId: number,
  baselineId: number,
  data: {
    title?: string;
    description?: string;
  },
): Promise<{
  data: API.Baseline;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/baselines/${baselineId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除基线 DELETE /api/projects/:projectId/baselines/:baselineId */
export function deleteBaseline(
  projectId: number,
  baselineId: number,
): Promise<{
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/baselines/${baselineId}`, {
    method: 'DELETE',
  });
}

/** 获取项目基线列表 GET /api/projects/:projectId/baselines */
export function getProjectBaselines(projectId: number): Promise<{
  data: API.Baseline[];
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/baselines`, {
    method: 'GET',
  });
}

/** 更新文档基线 PUT /api/documents/:documentId/baseline */
export function updateDocumentBaseline(
  documentId: string,
  data: {
    baselineId: number | null;
  },
): Promise<{
  success: boolean;
}> {
  return request(`/api/documents/${documentId}/baseline`, {
    method: 'PUT',
    data,
  });
}

/** 获取基线关联的文档列表 GET /api/projects/:projectId/baselines/:baselineId/documents */
export function getBaselineDocuments(
  projectId: number,
  baselineId: number,
): Promise<{
  data: API.ASPICEDocument[];
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/baselines/${baselineId}/documents`, {
    method: 'GET',
  });
}

/** 获取当前用户所有项目的产品库列表 GET /api/product-library */
export function getProductLibrary(): Promise<{
  data: Array<{
    projectId: number;
    projectName: string;
    documentCount: number;
    documents: API.ASPICEDocument[];
  }>;
  success: boolean;
}> {
  return request(`/api/product-library`, {
    method: 'GET',
  });
}

/** 将文档加入产品库 POST /api/documents/:documentId/product-library */
export function addToProductLibrary(documentId: string): Promise<{
  success: boolean;
}> {
  return request(`/api/documents/${documentId}/product-library`, {
    method: 'POST',
  });
}

/** 从产品库移除文档 DELETE /api/documents/:documentId/product-library */
export function removeFromProductLibrary(documentId: string): Promise<{
  success: boolean;
}> {
  return request(`/api/documents/${documentId}/product-library`, {
    method: 'DELETE',
  });
}

/** 下载项目产品库所有文档 GET /api/product-library/:projectId/download */
export function downloadProductLibrary(projectId: number): Promise<Blob> {
  return request(`/api/product-library/${projectId}/download`, {
    method: 'GET',
    responseType: 'blob',
  });
}

/** 获取问题解决列表 GET /api/projects/:projectId/problem-resolutions */
export function getProblemResolutions(
  projectId: number,
  params?: {
    processCode?: string;
    documentId?: number;
    sourceId?: number;
    discoverySource?: API.ProblemDiscoverySource;
    search?: string;
    code?: string;
    subject?: string;
    description?: string;
    documentName?: string;
    currentHandler?: string;
    status?: API.ProblemResolutionStatus | API.ProblemResolutionStatus[];
    current?: number;
    pageSize?: number;
  },
): Promise<{
  success: boolean;
  data: {
    list: API.ProblemResolution[];
    total: number;
    page: number;
    pageSize: number;
  };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions`, {
    method: 'GET',
    params,
  });
}

/** 获取我的问题待办（跨项目） GET /api/problems/my-todo */
export function getMyTodoProblems(params?: {
  search?: string;
  current?: number;
  pageSize?: number;
}): Promise<{
  success: boolean;
  data: {
    list: API.ProblemTodoItem[];
    total: number;
    page: number;
    pageSize: number;
  };
  message?: string;
}> {
  return request('/api/problems/my-todo', {
    method: 'GET',
    params,
  });
}

export type ProblemResolutionPayload = {
  subject: string;
  description: string;
  severity: API.SeverityGrade;
  priority: API.PriorityGrade;
  processCode: string;
  documentId: number;
  sourceId?: number;
  discoverySource?: API.ProblemDiscoverySource;
  remark?: string;
  relatedCase?: string;
  similarProblemId?: number;
  file?: File;
};

/** 获取问题解决详情 GET /api/projects/:projectId/problem-resolutions/:problemId */
export function getProblemResolution(
  projectId: number,
  problemId: number,
): Promise<{
  data: API.ProblemResolution;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}`, {
    method: 'GET',
  });
}

/** 创建问题解决 POST /api/projects/:projectId/problem-resolutions */
export function createProblemResolution(
  projectId: number,
  data: ProblemResolutionPayload,
): Promise<{
  data: API.ProblemResolution;
  success: boolean;
  message?: string;
}> {
  if (data.file) {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('description', data.description);
    formData.append('severity', data.severity);
    formData.append('priority', data.priority);
    formData.append('processCode', data.processCode);
    formData.append('documentId', String(data.documentId));
    if (data.sourceId != null) {
      formData.append('sourceId', String(data.sourceId));
    }
    if (data.discoverySource) {
      formData.append('discoverySource', data.discoverySource);
    }
    if (data.remark) {
      formData.append('remark', data.remark);
    }
    if (data.relatedCase) {
      formData.append('relatedCase', data.relatedCase);
    }
    if (data.similarProblemId != null) {
      formData.append('similarProblemId', String(data.similarProblemId));
    }
    formData.append('file', data.file);
    return request(`/api/projects/${projectId}/problem-resolutions`, {
      method: 'POST',
      data: formData,
    });
  }

  const { file: _file, ...jsonData } = data;
  return request(`/api/projects/${projectId}/problem-resolutions`, {
    method: 'POST',
    data: jsonData,
  });
}

/** 更新问题解决 PUT /api/projects/:projectId/problem-resolutions/:problemId */
export function updateProblemResolution(
  projectId: number,
  problemId: number,
  data: ProblemResolutionPayload,
): Promise<{
  data: API.ProblemResolution;
  success: boolean;
  message?: string;
}> {
  if (data.file) {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('description', data.description);
    formData.append('severity', data.severity);
    formData.append('priority', data.priority);
    formData.append('processCode', data.processCode);
    formData.append('documentId', String(data.documentId));
    if (data.remark) {
      formData.append('remark', data.remark);
    }
    if (data.relatedCase) {
      formData.append('relatedCase', data.relatedCase);
    }
    if (data.similarProblemId != null) {
      formData.append('similarProblemId', String(data.similarProblemId));
    }
    formData.append('file', data.file);
    return request(`/api/projects/${projectId}/problem-resolutions/${problemId}`, {
      method: 'PUT',
      data: formData,
    });
  }

  const { file: _file, sourceId: _sourceId, ...jsonData } = data;
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}`, {
    method: 'PUT',
    data: jsonData,
  });
}

export type AssignProblemResolutionPayload = {
  handlerId: number;
  remark?: string;
};

/** 分配问题解决 POST /api/projects/:projectId/problem-resolutions/:problemId/assign */
export function assignProblemResolution(
  projectId: number,
  problemId: number,
  data: AssignProblemResolutionPayload,
): Promise<{
  data: API.ProblemResolution;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}/assign`, {
    method: 'POST',
    data,
  });
}

export type ProblemWorkflowAction =
  | 'NO_MODIFICATION'
  | 'START_PROCESSING'
  | 'SUBMIT_REVIEW'
  | 'REVIEW_PASS'
  | 'REVIEW_FAIL'
  | 'CLOSE'
  | 'REJECT'
  | 'REOPEN';

export type ProblemWorkflowPayload = {
  action: ProblemWorkflowAction;
  isProblem?: boolean;
  plannedCompletionDate?: string;
  rootCause?: string;
  temporaryAction?: string;
  solution?: string;
  workHours?: number;
  resolutionDocumentId?: number;
  handlerId?: number;
  priority?: API.PriorityGrade;
  processCode?: string;
  documentId?: number;
  /** 关闭时写入的文档。列表传文档族最新版本，文档详情传当前文档。 */
  closedDocumentId?: number;
  remark?: string;
  comment?: string;
  file?: File;
};

/** 问题处理工作流 POST /api/projects/:projectId/problem-resolutions/:problemId/actions */
export function applyProblemResolutionAction(
  projectId: number,
  problemId: number,
  data: ProblemWorkflowPayload,
): Promise<{
  data: API.ProblemResolution;
  success: boolean;
  message?: string;
}> {
  if (data.file) {
    const formData = new FormData();
    formData.append('action', data.action);
    if (data.remark) {
      formData.append('remark', data.remark);
    }
    if (data.comment) {
      formData.append('comment', data.comment);
    }
    if (data.closedDocumentId != null) {
      formData.append('closedDocumentId', String(data.closedDocumentId));
    }
    formData.append('file', data.file);
    return request(`/api/projects/${projectId}/problem-resolutions/${problemId}/actions`, {
      method: 'POST',
      data: formData,
    });
  }

  const { file: _file, ...jsonData } = data;
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}/actions`, {
    method: 'POST',
    data: jsonData,
  });
}

/** 删除问题解决 DELETE /api/projects/:projectId/problem-resolutions/:problemId */
export function deleteProblemResolution(
  projectId: number,
  problemId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}`, {
    method: 'DELETE',
  });
}

/** 下载问题历史附件 GET /api/projects/:projectId/problem-resolutions/:problemId/histories/:historyId/attachment */
export function downloadProblemHistoryAttachment(
  projectId: number,
  problemId: number,
  historyId: number,
): Promise<Blob> {
  return request(
    `/api/projects/${projectId}/problem-resolutions/${problemId}/histories/${historyId}/attachment`,
    {
      method: 'GET',
      responseType: 'blob',
      fetchOptions: {
        skipErrorHandler: true,
      },
    } as any,
  );
}

/** 获取问题意见 GET /api/projects/:projectId/problem-resolutions/:problemId/comments */
export function getProblemComments(
  projectId: number,
  problemId: number,
): Promise<{
  success: boolean;
  data: API.ProblemComment[];
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}/comments`, {
    method: 'GET',
  });
}

/** 新增问题意见 POST /api/projects/:projectId/problem-resolutions/:problemId/comments */
export function createProblemComment(
  projectId: number,
  problemId: number,
  content: string,
): Promise<{
  success: boolean;
  data: API.ProblemComment;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-resolutions/${problemId}/comments`, {
    method: 'POST',
    data: { content },
  });
}

/** 更新问题意见 PUT /api/projects/:projectId/problem-resolutions/:problemId/comments/:commentId */
export function updateProblemComment(
  projectId: number,
  problemId: number,
  commentId: number,
  content: string,
): Promise<{
  success: boolean;
  data: API.ProblemComment;
  message?: string;
}> {
  return request(
    `/api/projects/${projectId}/problem-resolutions/${problemId}/comments/${commentId}`,
    {
      method: 'PUT',
      data: { content },
    },
  );
}

/** 删除问题意见 DELETE /api/projects/:projectId/problem-resolutions/:problemId/comments/:commentId */
export function deleteProblemComment(
  projectId: number,
  problemId: number,
  commentId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(
    `/api/projects/${projectId}/problem-resolutions/${problemId}/comments/${commentId}`,
    {
      method: 'DELETE',
    },
  );
}

export type ProblemCheckPayload = {
  processCode: string;
  criteria: string;
  hasDeviation: boolean;
  subject?: string;
  description?: string;
  severity?: API.SeverityGrade;
  priority?: API.PriorityGrade;
  documentId?: number;
};

/** 获取问题检查列表 GET /api/projects/:projectId/problem-checks */
export function getProblemChecks(
  projectId: number,
  params?: {
    processCode?: string;
    search?: string;
    current?: number;
    pageSize?: number;
  },
): Promise<{
  success: boolean;
  data: {
    list: API.ProblemCheck[];
    total: number;
    page: number;
    pageSize: number;
  };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-checks`, {
    method: 'GET',
    params,
  });
}

/** 创建问题检查 POST /api/projects/:projectId/problem-checks */
export function createProblemCheck(
  projectId: number,
  data: ProblemCheckPayload,
): Promise<{
  data: API.ProblemCheck;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-checks`, {
    method: 'POST',
    data,
  });
}

/** 更新问题检查 PUT /api/projects/:projectId/problem-checks/:checkId */
export function updateProblemCheck(
  projectId: number,
  checkId: number,
  data: ProblemCheckPayload,
): Promise<{
  data: API.ProblemCheck;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-checks/${checkId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除问题检查 DELETE /api/projects/:projectId/problem-checks/:checkId */
export function deleteProblemCheck(
  projectId: number,
  checkId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/problem-checks/${checkId}`, {
    method: 'DELETE',
  });
}

/** 质量检查表条目入参 */
export type QaChecklistItemInput = {
  seq?: number;
  clause?: string;
  category?: string;
  text: string;
};

/** 获取项目质量检查表 GET /api/projects/:projectId/qa-checklists */
export function getQaChecklists(
  projectId: number,
  params?: { processCode?: string },
): Promise<{
  success: boolean;
  data: API.QaChecklist[];
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists`, {
    method: 'GET',
    params,
  });
}

/** 获取检查表详情（条目 + 检查记录） GET /api/projects/:projectId/qa-checklists/:checklistId */
export function getQaChecklistDetail(
  projectId: number,
  checklistId: number,
): Promise<{
  success: boolean;
  data: {
    checklist: API.QaChecklist;
    items: API.QaChecklistItem[];
    summary: API.QaChecklistSummary;
  };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists/${checklistId}`, {
    method: 'GET',
  });
}

/** 上传/更新当前过程的检查表 POST /api/projects/:projectId/qa-checklists */
export function saveQaChecklist(
  projectId: number,
  data: {
    processCode: string;
    name?: string;
    version?: string;
    sourceFilename?: string;
    remark?: string;
    items: QaChecklistItemInput[];
  },
): Promise<{
  success: boolean;
  data: API.QaChecklist;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists`, {
    method: 'POST',
    data,
  });
}

/** 删除整套检查表 DELETE /api/projects/:projectId/qa-checklists/:checklistId */
export function deleteQaChecklist(
  projectId: number,
  checklistId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists/${checklistId}`, {
    method: 'DELETE',
  });
}

/** 写入某条检查条目的检查结果 PUT /api/projects/:projectId/qa-checklist-items/:itemId/result
 *
 * 带 recordId = 修改指定轮次；不带 = 追加新一轮（复检留痕）。
 */
export function saveQaCheckItemResult(
  projectId: number,
  itemId: number,
  data: {
    verdict: API.QaCheckVerdict;
    evidence?: string;
    documentId?: number;
    subject?: string;
    description?: string;
    severity?: API.SeverityGrade;
    priority?: API.PriorityGrade;
    /** 指定要修改的检查轮次；不传表示新增一轮 */
    recordId?: number;
  },
): Promise<{
  success: boolean;
  data: {
    record: API.ProblemCheck;
    item: API.QaChecklistItem;
    problem?: API.ProblemSourceBrief | null;
  };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklist-items/${itemId}/result`, {
    method: 'PUT',
    data,
  });
}

/** 清除某条检查条目的检查轮次 DELETE /api/projects/:projectId/qa-checklist-items/:itemId/result
 *
 * 带 recordId = 删除指定轮次；不传 = 删除最新一轮。
 */
export function clearQaCheckItemResult(
  projectId: number,
  itemId: number,
  recordId?: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  const query = recordId ? `?recordId=${recordId}` : '';
  return request(`/api/projects/${projectId}/qa-checklist-items/${itemId}/result${query}`, {
    method: 'DELETE',
  });
}

/**
 * AI 预检：按检查表条目检查一份文档 POST /api/projects/:projectId/qa-checklists/ai-precheck
 *
 * 返回的只有「疑似不满足 / 无法判断」两类疑点 + 原文证据；AI 不会给出「符合」的结论。
 */
export function runQaAiPrecheck(
  projectId: number,
  data: {
    processCode: string;
    documentId: number;
    itemIds?: number[];
    onlyPending?: boolean;
    lang?: string;
  },
): Promise<{
  success: boolean;
  data: API.QaAiPrecheckData;
  message?: string;
  code?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists/ai-precheck`, {
    method: 'POST',
    data,
  });
}

/** 写入勾选的 AI 建议 POST /api/projects/:projectId/qa-checklists/ai-precheck/apply */
export function applyQaAiPrecheck(
  projectId: number,
  data: {
    processCode: string;
    documentId?: number;
    results: {
      itemId: number;
      aiVerdict: API.QaAiVerdict;
      aiEvidence?: string | null;
    }[];
  },
): Promise<{
  success: boolean;
  data: { applied: number; itemIds: number[] };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklists/ai-precheck/apply`, {
    method: 'POST',
    data,
  });
}

/** 忽略某条条目的 AI 建议 DELETE /api/projects/:projectId/qa-checklist-items/:itemId/ai-precheck */
export function clearQaAiPrecheck(
  projectId: number,
  itemId: number,
): Promise<{
  success: boolean;
  data?: { cleared: boolean };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklist-items/${itemId}/ai-precheck`, {
    method: 'DELETE',
  });
}

/**
 * 列出项目的质量保证归档件（外部检查表 / QA 记录）
 * GET /api/projects/:projectId/qa-checklist-documents
 *
 * 归档件与系统检查表完全隔离：不参与执行视图统计、AI 预检与问题台账。
 * `kind` 缺省为 EXTERNAL_CHECKLIST（外部检查表）。
 */
export function getQaChecklistDocuments(
  projectId: number,
  params?: { processCode?: string; kind?: API.QaChecklistDocKind },
): Promise<{
  success: boolean;
  data: API.QaChecklistDocument[];
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklist-documents`, {
    method: 'GET',
    params,
  });
}

/** 上传质量保证归档原件（外部检查表 / QA 记录） POST /api/projects/:projectId/qa-checklist-documents */
export function uploadQaChecklistDocument(
  projectId: number,
  data: {
    processCode: string;
    /** 单文件（与 files 二选一，保留给老调用方） */
    file?: File;
    /** 批量上传：非空时优先于 file，服务端一次事务写多条 */
    files?: File[];
    /** 归档夹分类，缺省为 EXTERNAL_CHECKLIST */
    kind?: API.QaChecklistDocKind;
    title?: string;
    /**
     * 逐份标题（按 files 顺序）。批量上传时前端为每个文件派生出
     * 「文件名去扩展名」并允许用户逐份改写，服务端逐份取用；
     * 缺位或空串时回落到 title / 文件名。
     */
    titles?: string[];
    /**
     * 逐份版本号（按 files 顺序）。同一批里的文档版本常常不同，
     * 所以版本跟着文件走；缺位或空串时回落到 version（单文件老调用方）。
     */
    versions?: string[];
    version?: string;
    remark?: string;
    /** 审核阶段（如 立项 / 策划 / 设计 / 验证），选填 */
    reviewStage?: string;
    /** 审核类型：自由填写（如「过程审核」「客户审核」），选填 */
    reviewType?: string;
    /** 是否含线下已填的检查记录（仅标注展示） */
    hasRecord?: boolean;
  },
): Promise<{
  success: boolean;
  /** 第一条记录（单文件时即该文件），兼容既有调用方 */
  data: API.QaChecklistDocument;
  /** 批量上传时的全部记录 */
  items?: API.QaChecklistDocument[];
  count?: number;
  message?: string;
}> {
  const formData = new FormData();
  const files = data.files?.length ? data.files : data.file ? [data.file] : [];
  files.forEach((file) => formData.append('files', file));
  formData.append('processCode', data.processCode);
  if (data.kind) {
    formData.append('kind', data.kind);
  }
  if (data.title) {
    formData.append('title', data.title);
  }
  if (data.titles?.length) {
    formData.append('titles', JSON.stringify(data.titles));
  }
  if (data.versions?.length) {
    formData.append('versions', JSON.stringify(data.versions));
  }
  if (data.version) {
    formData.append('version', data.version);
  }
  if (data.remark) {
    formData.append('remark', data.remark);
  }
  if (data.reviewStage) {
    formData.append('reviewStage', data.reviewStage);
  }
  if (data.reviewType) {
    formData.append('reviewType', data.reviewType);
  }
  formData.append('hasRecord', data.hasRecord ? 'true' : 'false');

  return request(`/api/projects/${projectId}/qa-checklist-documents`, {
    method: 'POST',
    data: formData,
  });
}

/**
 * 取归档件原件（预览用） GET /api/projects/:projectId/qa-checklist-documents/:documentId/file
 *
 * 走带鉴权的接口而非 static 直链，归档件不会出现「知道 URL 即可下载」的漏洞。
 */
export function getQaChecklistDocumentFile(projectId: number, documentId: number): Promise<Blob> {
  return request(`/api/projects/${projectId}/qa-checklist-documents/${documentId}/file`, {
    method: 'GET',
    responseType: 'blob',
  });
}

/**
 * 删除归档件（记录 + 磁盘原件）
 * DELETE /api/projects/:projectId/qa-checklist-documents/:documentId?kind=...
 *
 * 必须带上与记录一致的分类，避免跨归档夹误删。
 */
export function deleteQaChecklistDocument(
  projectId: number,
  documentId: number,
  kind?: API.QaChecklistDocKind,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(
    `/api/projects/${projectId}/qa-checklist-documents/${documentId}${kind ? `?kind=${kind}` : ''}`,
    {
      method: 'DELETE',
    },
  );
}

/**
 * 批量删除归档件（记录 + 磁盘原件），全成或全不成
 * POST /api/projects/:projectId/qa-checklist-documents/batch-delete
 *
 * 必须带上与记录一致的分类；只要其中一条不属于该分类或不存在，
 * 服务端整批拒绝（400）且一条都不删，不会出现「删了一半」的中间状态。
 */
export function batchDeleteQaChecklistDocuments(
  projectId: number,
  ids: number[],
  kind?: API.QaChecklistDocKind,
): Promise<{
  success: boolean;
  /** 实际删除条数 */
  deleted?: number;
  /** 记录已删但磁盘文件残留时的告警（不影响删除结果） */
  fileWarnings?: string[];
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-checklist-documents/batch-delete`, {
    method: 'POST',
    data: { ids, kind },
  });
}

/**
 * 质量保证报告：跨过程汇总 SUP.1 的检查执行结果与问题台账
 * GET /api/projects/:projectId/qa-report
 */
export function getQaReport(projectId: number): Promise<{
  success: boolean;
  data: API.QaReport;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-report`, {
    method: 'GET',
  });
}

/**
 * 列出项目的 QA 不符合项台账 GET /api/projects/:projectId/qa-nonconformities
 *
 * 与检查表执行派生的问题台账完全隔离，仅 SUP.1 过程负责人可增删改。
 */
export function getQaNonconformities(
  projectId: number,
  params?: { processCode?: string },
): Promise<{
  success: boolean;
  data: API.QaNonconformity[];
  message?: string;
}> {
  const query = new URLSearchParams();
  if (params?.processCode) query.set('processCode', params.processCode);
  const qs = query.toString();
  return request(`/api/projects/${projectId}/qa-nonconformities${qs ? `?${qs}` : ''}`, {
    method: 'GET',
  });
}

/** QA 不符合项的新增/编辑载荷（processCode 仅新增时需要） */
export type QaNonconformityPayload = Omit<API.QaNonconformity, 'id' | 'projectId'> & {
  processCode?: string;
};

/** 新增 QA 不符合项 POST /api/projects/:projectId/qa-nonconformities（仅 SUP.1 负责人） */
export function createQaNonconformity(
  projectId: number,
  data: QaNonconformityPayload,
): Promise<{
  success: boolean;
  data: API.QaNonconformity;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-nonconformities`, {
    method: 'POST',
    data,
  });
}

/** 修改 QA 不符合项 PUT /api/projects/:projectId/qa-nonconformities/:id（仅 SUP.1 负责人） */
export function updateQaNonconformity(
  projectId: number,
  recordId: number,
  data: Partial<QaNonconformityPayload>,
): Promise<{
  success: boolean;
  data: API.QaNonconformity;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-nonconformities/${recordId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除 QA 不符合项 DELETE /api/projects/:projectId/qa-nonconformities/:id（仅 SUP.1 负责人） */
export function deleteQaNonconformity(
  projectId: number,
  recordId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/qa-nonconformities/${recordId}`, {
    method: 'DELETE',
  });
}

/** 获取项目变更请求列表 GET /api/projects/:projectId/change-requests */
export function getChangeRequests(
  projectId: number,
  params?: {
    search?: string;
    current?: number;
    pageSize?: number;
    status?: API.ChangeRequestStatus;
  },
): Promise<{
  success: boolean;
  data: {
    list: API.ChangeRequest[];
    page: number;
    pageSize: number;
    total: number;
  };
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-requests`, {
    method: 'GET',
    params,
  });
}

/** 创建变更请求 POST /api/projects/:projectId/change-requests */
export function createChangeRequest(
  projectId: number,
  data: API.CreateChangeRequestParams,
): Promise<{
  data: API.ChangeRequest;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/change-requests`, {
    method: 'POST',
    data,
  });
}

/** 上传变更请求附件 POST /api/projects/:projectId/change-requests/:crId/upload */
export function uploadChangeRequestAttachment(
  projectId: number,
  crId: number,
  file: File,
): Promise<{
  success: boolean;
  data?: string;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/api/projects/${projectId}/change-requests/${crId}/upload`, {
    method: 'POST',
    data: formData,
  });
}

/** 下载变更请求附件 GET /api/projects/:projectId/change-requests/:crId/attachment */
export function downloadChangeRequestAttachment(projectId: number, crId: number): Promise<Blob> {
  return request(`/api/projects/${projectId}/change-requests/${crId}/attachment`, {
    method: 'GET',
    responseType: 'blob',
    fetchOptions: {
      skipErrorHandler: true,
    },
  } as any);
}

/** 更新变更请求 PUT /api/projects/:projectId/change-requests/:crId */
export function updateChangeRequest(
  projectId: number,
  crId: number,
  data: API.UpdateChangeRequestParams,
): Promise<{
  data: API.ChangeRequest;
  success: boolean;
}> {
  return request(`/api/projects/${projectId}/change-requests/${crId}`, {
    method: 'PUT',
    data,
  });
}

/** 升级文档（复制创建新版本并写入 impact_processes）*/
export function upgradeDocument(
  projectId: number,
  docId: number,
  data: { cr_ids?: number[]; version?: string; customDocumentCode?: string },
): Promise<{ data: API.ASPICEDocument; success: boolean }> {
  return request(`/api/projects/${projectId}/documents/${docId}/upgrade`, {
    method: 'POST',
    data,
  });
}

/** 删除变更请求 DELETE /api/projects/:projectId/change-requests/:crId */
export function deleteChangeRequest(
  projectId: number,
  crId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-requests/${crId}`, {
    method: 'DELETE',
  });
}

/** 创建变更请求清单 POST /api/projects/:projectId/change-request-summaries */
export function createChangeRequestSummary(
  projectId: number,
  data: {
    version: string;
    changeRequests: number[];
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-request-summaries`, {
    method: 'POST',
    data,
  });
}

/** 更新变更请求清单 PUT /api/projects/:projectId/change-request-summaries/:documentId */
export function updateChangeRequestSummary(
  projectId: number,
  data: {
    documentId: number;
    version: string;
    changeRequests: number[];
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-request-summaries/${data.documentId}`, {
    method: 'PUT',
    data: {
      version: data.version,
      changeRequests: data.changeRequests,
    },
  });
}

/** 审核变更请求 POST /api/projects/:projectId/change-requests/:crId/review */
export function reviewChangeRequestDocument(
  projectId: number,
  crId: number,
  data: {
    reviews: Array<{
      processCode: string;
      result: 'YES' | 'NO';
      documents?: Array<{ id: number; description: string }>;
    }>;
  },
): Promise<{
  data: API.ChangeRequest;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-requests/${crId}/review`, {
    method: 'POST',
    data,
  });
}

/** 验证变更请求 POST /api/projects/:projectId/change-requests/:crId/verify */
export function verifyChangeRequest(
  projectId: number,
  crId: number,
  data: {
    result: 'COMPLETED' | 'PARTIAL';
    resultRemark?: string;
  },
): Promise<{
  data: API.ChangeRequest;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/change-requests/${crId}/verify`, {
    method: 'POST',
    data,
  });
}

/** 上传企业Logo POST /api/system/company/logo
 * 将logo图片直接存储到MongoDB数据库
 */
export function uploadCompanyLogo(file: File): Promise<{
  success: boolean;
  url: string;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request('/api/system/company/logo', {
    method: 'POST',
    data: formData,
  });
}

/** 获取企业Logo GET /api/system/company/logo
 * 从MongoDB中获取logo图片，返回带有时间戳的URL以防止缓存问题
 */
export function getCompanyLogo(): Promise<{
  success: boolean;
  data: string; // logo URL with timestamp
}> {
  return request('/api/system/company/logo', {
    method: 'GET',
  });
}

/** 获取文档的所有评审意见 GET /api/documents/:docId/review-comments */
export function getReviewComments(docId: string): Promise<{
  success: boolean;
  data: API.ReviewComment[];
  history?: API.ReviewHistory[];
}> {
  return request(`/api/documents/${docId}/review-comments`, {
    method: 'GET',
  });
}

/** 添加评审意见 POST /api/documents/:docId/review-comments */
export function addReviewComment(
  docId: string,
  data: {
    comment: string;
    position?: string;
    subject?: string;
    severity?: API.SeverityGrade;
    priority?: API.PriorityGrade;
    role: 'REVIEWER' | 'REVIEW_LEADER' | 'AUDITOR' | 'APPROVER';
  },
): Promise<{
  success: boolean;
  data: API.ReviewComment;
  message?: string;
}> {
  return request(`/api/documents/${docId}/review-comments`, {
    method: 'POST',
    data,
  });
}

/** 更新评审意见 PUT /api/documents/:docId/review-comments/:commentId */
export function updateReviewComment(
  docId: string,
  commentId: number,
  data: {
    comment: string;
    position?: string;
    subject?: string;
    severity?: API.SeverityGrade;
    priority?: API.PriorityGrade;
  },
): Promise<{
  success: boolean;
  data: API.ReviewComment;
  message?: string;
}> {
  return request(`/api/documents/${docId}/review-comments/${commentId}`, {
    method: 'PUT',
    data,
  });
}

/** 删除评审意见 DELETE /api/documents/:docId/review-comments/:commentId */
export function deleteReviewComment(
  docId: string,
  commentId: number,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${docId}/review-comments/${commentId}`, {
    method: 'DELETE',
  });
}

/** 判断当前文档是否仍有未关闭的问题 GET /api/documents/:docId/unclosed-problems */
export function getDocumentUnclosedProblems(docId: string): Promise<{
  success: boolean;
  message?: string;
  data?: {
    hasUnclosed: boolean;
    message?: string | null;
  };
}> {
  return request(`/api/documents/${docId}/unclosed-problems`, {
    method: 'GET',
  });
}

/** 更新评审状态 PUT /api/documents/:docId/review-status */
export function updateReviewStatus(
  docId: string,
  data: {
    status: string;
    role: 'REVIEWER' | 'REVIEW_LEADER' | 'AUDITOR' | 'APPROVER';
    conditional?: boolean;
    returnTo?: 'OWNER' | 'REVIEW_LEADER' | 'AUDITOR';
  },
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    documentStatus?: string;
    revertedToDraft?: boolean;
    upgradedDocumentId?: number;
    upgradedVersion?: string;
    returnTo?: string;
  };
}> {
  return request(`/api/documents/${docId}/review-status`, {
    method: 'PUT',
    data,
  });
}

/** 评审组长、审核者或批准者确认退回 PUT /api/documents/:docId/review-return */
export function returnReview(
  docId: string,
  data: {
    role: 'REVIEW_LEADER' | 'AUDITOR' | 'APPROVER';
    returnTo: 'OWNER' | 'REVIEW_LEADER' | 'AUDITOR';
  },
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    documentStatus?: string;
    revertedToDraft?: boolean;
    upgradedDocumentId?: number;
    upgradedVersion?: string;
    returnTo?: string;
  };
}> {
  return request(`/api/documents/${docId}/review-return`, {
    method: 'PUT',
    data,
  });
}

/** 将文档转为草稿 PUT /api/documents/:docId/revert-to-draft */
export function revertDocumentToDraft(docId: string): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${docId}/revert-to-draft`, {
    method: 'PUT',
  });
}

/** 负责人撤回评审 PUT /api/documents/:docId/withdraw-review */
export function withdrawReview(docId: string): Promise<{
  success: boolean;
  message?: string;
  data?: {
    documentStatus?: string;
  };
}> {
  return request(`/api/documents/${docId}/withdraw-review`, {
    method: 'PUT',
  });
}

/** 评审组成员结束评审 PUT /api/documents/:docId/complete-review */
export function completeReviewerReview(docId: string): Promise<{
  success: boolean;
  message?: string;
  data?: {
    documentStatus?: string;
    revertedToDraft?: boolean;
    upgradedDocumentId?: number;
    upgradedVersion?: string;
  };
}> {
  return request(`/api/documents/${docId}/complete-review`, {
    method: 'PUT',
  });
}

/** 上传文档插图 POST /api/documents/:docId/images */
export function uploadDocumentImage(
  docId: string,
  file: File,
): Promise<{
  success: boolean;
  data?: string;
  message?: string;
  filename?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/api/documents/${docId}/images`, {
    method: 'POST',
    data: formData,
  });
}

/** 删除文档插图 DELETE /api/documents/:docId/images/:filename */
export function deleteDocumentImage(
  docId: string,
  fileUrl: string,
): Promise<{
  success: boolean;
  message?: string;
}> {
  let filename = '';
  try {
    const u = new URL(fileUrl, window.location.origin);
    filename = decodeURIComponent(u.pathname.split('/').pop() || '');
  } catch {
    filename = decodeURIComponent(fileUrl.split('/').pop() || '');
  }

  if (!filename) {
    return Promise.resolve({
      success: false,
      message: '非法的 fileUrl',
    });
  }

  return request(`/api/documents/${docId}/images/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });
}

/** 上传文件到项目 POST /api/projects/:projectId/upload */
export function uploadFileToProject(
  projectId: number,
  file: File,
): Promise<{
  success: boolean;
  data?: string;
  message?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);

  return request(`/api/projects/${projectId}/upload`, {
    method: 'POST',
    data: formData,
  });
}

/** 删除项目已上传的文件 DELETE /api/projects/:projectId/files/:filename */
export function deleteFileFromProject(
  projectId: number,
  fileUrl: string,
): Promise<{
  success: boolean;
  message?: string;
}> {
  let filename = '';
  try {
    const u = new URL(fileUrl, window.location.origin);
    filename = decodeURIComponent(u.pathname.split('/').pop() || '');
  } catch {
    filename = decodeURIComponent(fileUrl.split('/').pop() || '');
  }

  if (!filename) {
    return Promise.resolve({
      success: false,
      message: '非法的 fileUrl',
    });
  }

  return request(`/api/projects/${projectId}/files/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });
}

/** AI 生成文档内容 POST /api/documents/:documentId/ai-generate */
export function generateAIContent(
  documentId: string,
  data: {
    documentContent: any;
    locationKey?: string;
    prompt: string;
  },
): Promise<{
  success: boolean;
  data?: {
    documentId: string;
    file_format: string;
    content: string;
  };
  message?: string;
}> {
  return request(`/api/documents/${documentId}/ai-generate`, {
    method: 'POST',
    data,
  });
}

/** 从 AI 创建文档 POST /api/projects/:projectId/documents/from-ai */
export function createDocumentFromAI(
  projectId: number,
  data: {
    processCode: string;
    categoryCode: string;
    templateId?: number;
    referenceContent?: string;
    userPrompt?: string;
    version?: string;
    customDocumentCode?: string;
  },
): Promise<{
  data: API.ASPICEDocument;
  success: boolean;
  message?: string;
}> {
  return request(`/api/projects/${projectId}/documents/from-ai`, {
    method: 'POST',
    data,
  });
}

/** 上传文档附件 POST /api/documents/:docId/attachments
 *
 * 两种上传格式：
 * 1) 解析内容上传（word/excel/txt）：传 JSON 数据，content 为解析后的结构化内容
 * 2) 二进制上传（other 类型）：传 multipart FormData（file + node_key 表单字段）
 *    后端按约定路径保存磁盘并在 MongoDB 中记录 file_path
 */
export function uploadDocumentAttachment(
  docId: string,
  payload:
    | {
        node_key: string;
        file_name: string;
        file_type: 'word' | 'excel' | 'txt';
        file_size: number;
        content: any;
      }
    | FormData,
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    id: string;
    file: string;
    type: string;
  };
}> {
  if (payload instanceof FormData) {
    return request(`/api/documents/${docId}/attachments`, {
      method: 'POST',
      data: payload as any,
    });
  }
  return request(`/api/documents/${docId}/attachments`, {
    method: 'POST',
    data: payload,
    headers: { 'Content-Type': 'application/json' },
  });
}

/** 下载文档附件 GET /api/documents/:docId/attachments/:attachmentId/download
 *
 * 返回二进制 Blob，调用方用 URL.createObjectURL 生成下载链接。
 * 跳过全局错误处理（skipErrorHandler）：word/excel 类型后端会返回 400 + JSON 错误，
 * 由调用方自行从 Blob 解析错误消息展示，避免双重提示。
 */
export function downloadDocumentAttachment(docId: string, attachmentId: string): Promise<Blob> {
  return request(`/api/documents/${docId}/attachments/${attachmentId}/download`, {
    method: 'GET',
    responseType: 'blob',
    fetchOptions: {
      skipErrorHandler: true,
    },
  } as any);
}

/** 获取文档附件列表 GET /api/documents/:docId/attachments */
export function getDocumentAttachments(
  docId: string,
  nodeKey?: string,
): Promise<{
  success: boolean;
  data?: Array<{
    id: string;
    file: string;
    type: string;
    node_key?: string;
  }>;
  message?: string;
}> {
  const params = new URLSearchParams();
  if (nodeKey) {
    params.append('node_key', nodeKey);
  }

  return request(
    `/api/documents/${docId}/attachments${params.toString() ? `?${params.toString()}` : ''}`,
    {
      method: 'GET',
    },
  );
}

/** 删除文档附件 DELETE /api/documents/:docId/attachments/:attachmentId */
export function deleteDocumentAttachment(
  docId: string,
  attachmentId: string,
): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/documents/${docId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

/** 批量删除多个节点key对应的附件 POST /api/documents/:docId/attachments/delete-by-nodes
 *
 * 用于删除文档节点时，连同该节点及其所有子孙节点关联的附件一并清理。
 */
export function deleteNodeAttachments(
  docId: string,
  nodeKeys: string[],
): Promise<{
  success: boolean;
  message?: string;
  data?: { deleted_count: number };
}> {
  return request(`/api/documents/${docId}/attachments/delete-by-nodes`, {
    method: 'POST',
    data: { node_keys: nodeKeys },
  });
}

/** 获取附件解析内容 GET /api/documents/:docId/attachments/:attachmentId/content */
export function getAttachmentContent(
  docId: string,
  attachmentId: string,
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> {
  return request(`/api/documents/${docId}/attachments/${attachmentId}/content`, {
    method: 'GET',
  });
}

/** 进入编辑前复制上游附件 POST /api/documents/:docId/attachments/:attachmentId/fork
 *
 * 附件已属于当前文档时返回原 id；否则复制一份并改写正文引用。
 */
export function forkDocumentAttachment(
  docId: string,
  attachmentId: string,
): Promise<{
  success: boolean;
  data?: { id: string; forked: boolean };
  message?: string;
}> {
  return request(`/api/documents/${docId}/attachments/${attachmentId}/fork`, {
    method: 'POST',
  });
}

/** 更新附件解析内容 PUT /api/documents/:docId/attachments/:attachmentId/content */
export function updateAttachmentContent(
  docId: string,
  attachmentId: string,
  content: any,
): Promise<{
  success: boolean;
  data?: { id: string; forked: boolean };
  message?: string;
}> {
  return request(`/api/documents/${docId}/attachments/${attachmentId}/content`, {
    method: 'PUT',
    data: { content },
  });
}

export type RecycleBinDocument = {
  id: number;
  title: string;
  documentCode?: string;
  customDocumentCode?: string;
  version?: string;
  status?: string;
  fileFormat?: string;
  processCode?: string;
  processName?: string;
  categoryCode?: string;
  deletedAt?: string;
  deletedBy?: string;
};

export type RecycleBinAttachment = {
  id: string;
  fileName?: string;
  fileType?: string;
  nodeKey?: string;
  docId: number;
  documentTitle?: string;
  documentCode?: string;
  processCode?: string;
  parentDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
};

/** 回收站文档列表 GET /api/recycle-bin/documents */
export function getRecycleBinDocuments(params: {
  projectId?: number;
  search?: string;
  current?: number;
  pageSize?: number;
}): Promise<{
  data: RecycleBinDocument[];
  success: boolean;
  total: number;
  message?: string;
}> {
  return request('/api/recycle-bin/documents', {
    method: 'GET',
    params,
  });
}

/** 回收站附件列表 GET /api/recycle-bin/attachments */
export function getRecycleBinAttachments(params: {
  projectId?: number;
  search?: string;
  current?: number;
  pageSize?: number;
}): Promise<{
  data: RecycleBinAttachment[];
  success: boolean;
  total: number;
  message?: string;
}> {
  return request('/api/recycle-bin/attachments', {
    method: 'GET',
    params,
  });
}

/** 恢复回收站文档 POST /api/recycle-bin/documents/:id/restore */
export function restoreRecycleBinDocument(id: number): Promise<{
  success: boolean;
  message?: string;
  warning?: string;
}> {
  return request(`/api/recycle-bin/documents/${id}/restore`, {
    method: 'POST',
  });
}

/** 彻底删除回收站文档 DELETE /api/recycle-bin/documents/:id */
export function purgeRecycleBinDocument(id: number): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/recycle-bin/documents/${id}`, {
    method: 'DELETE',
  });
}

/** 恢复回收站附件 POST /api/recycle-bin/attachments/:id/restore */
export function restoreRecycleBinAttachment(id: string): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/recycle-bin/attachments/${id}/restore`, {
    method: 'POST',
  });
}

/** 彻底删除回收站附件 DELETE /api/recycle-bin/attachments/:id */
export function purgeRecycleBinAttachment(id: string): Promise<{
  success: boolean;
  message?: string;
}> {
  return request(`/api/recycle-bin/attachments/${id}`, {
    method: 'DELETE',
  });
}
